import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  listVoiceTransferQueue,
  claimVoiceTransferQueue,
} from "../api/client";
import { useCallContext } from "../context/CallContext";
import { useWorkspaceConfig } from "../context/WorkspaceConfigContext";
import useDraggablePanel from "../hooks/useDraggablePanel";

const COLLAPSED_KEY = "xpro-voice-queue-collapsed";

function waitLabel(enqueuedAt, nowMs) {
  if (!enqueuedAt) return "";
  const raw = String(enqueuedAt).trim();
  const parsed = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw) || raw.includes("T")
    ? new Date(raw)
    : new Date(raw.replace(" ", "T") + "-03:00");
  const ms = nowMs - parsed.getTime();
  if (!Number.isFinite(ms) || ms < 0) return "agora";
  const sec = Math.floor(ms / 1000);
  if (sec < 10) return "agora";
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  if (min < 60) return rem ? `${min}m ${rem}s` : `${min}m`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}m`;
}

function defaultPanelPos() {
  if (typeof window === "undefined") return { x: 16, y: 16 };
  return {
    x: Math.max(8, window.innerWidth - 340),
    y: Math.max(8, window.innerHeight - 220),
  };
}

function readCollapsedPref() {
  try {
    return sessionStorage.getItem(COLLAPSED_KEY) === "1";
  } catch (_) {
    return false;
  }
}

/**
 * Painel flutuante da fila de voz — só aparece com alguém na linha.
 * Um clique em "Falar agora" puxa e conecta (sem Aceitar de novo).
 */
export default function VoiceQueuePanel({ refreshToken = 0 }) {
  const navigate = useNavigate();
  const { companyKey } = useWorkspaceConfig();
  const hasTenantCompany = Boolean(String(companyKey || "").trim());
  const {
    armSoftphoneAutoAccept,
    clearSoftphoneAutoAccept,
    markBusyForQueueClaim,
    connectingToLead,
    outgoingCall,
    softphoneError,
    clearSoftphoneError,
  } = useCallContext();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ personal: 0, general: 0, total: 0 });
  const [hydrated, setHydrated] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [collapsed, setCollapsed] = useState(readCollapsedPref);
  const prevTotalRef = useRef(0);
  const inFlightRef = useRef(false);

  const drag = useDraggablePanel("xpro-voice-queue-pos", defaultPanelPos);

  const isConnecting =
    Boolean(claimingId) ||
    (Number(connectingToLead) > 0 &&
      Number(outgoingCall?.leadId || 0) !== Number(connectingToLead));

  const reload = useCallback(async () => {
    if (!hasTenantCompany) {
      setItems([]);
      setCounts({ personal: 0, general: 0, total: 0 });
      setError(null);
      setHydrated(true);
      prevTotalRef.current = 0;
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const data = await listVoiceTransferQueue();
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      const nextCounts = data?.counts || { personal: 0, general: 0, total: 0 };
      setItems(nextItems);
      setCounts(nextCounts);
      setError(null);
      if (nextCounts.total > 0 && prevTotalRef.current === 0) {
        setCollapsed(false);
      }
      prevTotalRef.current = nextCounts.total || 0;
    } catch (err) {
      setItems([]);
      setCounts({ personal: 0, general: 0, total: 0 });
      setError(err?.message || null);
      prevTotalRef.current = 0;
    } finally {
      setHydrated(true);
      inFlightRef.current = false;
    }
  }, [hasTenantCompany]);

  useEffect(() => {
    void reload();
  }, [reload, refreshToken]);

  useEffect(() => {
    if (!hasTenantCompany) return undefined;
    const t = setInterval(() => {
      void reload();
    }, 20000);
    return () => clearInterval(t);
  }, [hasTenantCompany, reload]);

  useEffect(() => {
    if (!counts.total && !isConnecting) return undefined;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [counts.total, isConnecting]);

  useEffect(() => {
    try {
      sessionStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch (_) {
      /* ignore */
    }
  }, [collapsed]);

  // Timeout de segurança se a ligação não chegar ao softphone.
  useEffect(() => {
    if (!connectingToLead) return undefined;
    const t = setTimeout(() => {
      clearSoftphoneAutoAccept?.();
      setError("Não conseguimos conectar a tempo. Tente de novo.");
    }, 40_000);
    return () => clearTimeout(t);
  }, [clearSoftphoneAutoAccept, connectingToLead]);

  const onClaim = async (item) => {
    if (!item?.channelId || !item?.canClaim) return;
    setClaimingId(item.id);
    setError(null);
    setInfo(null);
    setCollapsed(false);
    setItems((prev) => prev.filter((row) => Number(row.id) !== Number(item.id)));
    setCounts((prev) => {
      const isGeneral = String(item.queueScope) === "general";
      return {
        personal: Math.max(0, (prev.personal || 0) - (isGeneral ? 0 : 1)),
        general: Math.max(0, (prev.general || 0) - (isGeneral ? 1 : 0)),
        total: Math.max(0, (prev.total || 0) - 1),
      };
    });
    try {
      const result = await claimVoiceTransferQueue(item.channelId, {
        queueItemId: item.id,
      });
      const payload =
        result?.data && typeof result.data === "object"
          ? result.data
          : result;
      const leadId = Number(payload?.leadId || item.leadId || 0) || null;
      const callSid = String(payload?.callSid || item.callSid || "").trim() || null;
      const channelId = Number(payload?.channelId || item.channelId) || item.channelId;
      const usePstn =
        Boolean(payload?.usePstn) || String(payload?.answerPath || "") === "pstn_agent";
      markBusyForQueueClaim?.({ channelId, leadId, callSid });
      if (usePstn) {
        clearSoftphoneAutoAccept?.();
        const last4 = String(payload?.agentPstnMasked || "").replace(/\D/g, "");
        setInfo(
          last4
            ? `Atenda no celular (…${last4}) — a ligação está tocando no seu número.`
            : "Atenda no celular — a ligação está tocando no seu número."
        );
      } else {
        armSoftphoneAutoAccept?.({
          leadId: leadId || item.leadId,
          channelId,
          callSid,
        });
      }
      if (leadId) {
        navigate(`/chat/${encodeURIComponent(String(leadId))}`);
      }
      await reload();
    } catch (err) {
      clearSoftphoneAutoAccept?.();
      const raw = String(err?.message || err?.data?.reason || "").trim();
      let msg = "Não foi possível atender esta ligação.";
      if (raw === "not_in_pool") {
        msg =
          "Você não está na equipe da fila deste canal. Peça para incluir seu usuário (ou cargo) na configuração da fila de telefone.";
      } else if (raw === "not_available") {
        msg =
          "Para atender: deixe o app aberto (softphone) ou cadastre seu celular no perfil para receber a ligação na linha móvel.";
      } else if (raw === "item_unavailable") {
        msg = "Esta ligação saiu da fila ou o tempo de espera acabou.";
      } else if (raw === "already_claimed") {
        msg = "Esta ligação já foi atendida por outra pessoa.";
      } else if (raw === "empty") {
        msg = "Não há mais ninguém na fila.";
      } else if (raw) {
        msg = raw;
      }
      setError(msg);
      await reload();
    } finally {
      setClaimingId(null);
    }
  };

  const openLead = (leadId) => {
    if (!leadId) return;
    navigate(`/chat/${encodeURIComponent(String(leadId))}`);
  };

  const claimable = useMemo(
    () => items.filter((i) => i.canClaim),
    [items]
  );
  const waitingOnly = useMemo(
    () => items.filter((i) => !i.canClaim),
    [items]
  );

  const headline = useMemo(() => {
    if (isConnecting) return "Conectando…";
    const n = counts.total || 0;
    if (n <= 0) return "";
    if (n === 1) return "1 cliente na linha";
    return `${n} clientes na linha`;
  }, [counts.total, isConnecting]);

  const subtitle = useMemo(() => {
    if (isConnecting) return "Abrindo a conversa com o cliente";
    if (claimable.length > 0) {
      return claimable.length === 1
        ? "Toque em Falar agora — conectamos na hora"
        : "Escolha quem atender — um toque e você fala";
    }
    if (waitingOnly.length > 0) {
      return "Quando você estiver livre, a ligação chega aqui";
    }
    return "";
  }, [claimable.length, isConnecting, waitingOnly.length]);

  // Mantém o painel visível enquanto conecta (mesmo se a fila esvaziar).
  if (!hasTenantCompany) return null;
  if (!hydrated) return null;
  if (!counts.total && !isConnecting && !error && !info && !softphoneError) return null;

  const primaryClaim = claimable[0] || null;

  return (
    <Paper
      ref={drag.panelRef}
      elevation={8}
      role="dialog"
      aria-label={headline}
      sx={{
        position: "fixed",
        left: drag.position.x,
        top: drag.position.y,
        zIndex: 1390,
        width: collapsed ? 280 : 320,
        maxWidth: "calc(100vw - 16px)",
        borderRadius: 2.5,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        cursor: drag.dragging ? "grabbing" : "default",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 12px 40px rgba(0,0,0,0.55)"
            : "0 12px 40px rgba(15,23,42,0.18)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          py: 1,
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(46,125,50,0.22)" : "rgba(46,125,50,0.1)",
          borderBottom: collapsed ? 0 : "1px solid",
          borderColor: "divider",
          cursor: "grab",
          userSelect: "none",
          "&:active": { cursor: "grabbing" },
        }}
        onMouseDown={drag.onDragHandleMouseDown}
        onTouchStart={drag.onDragHandleTouchStart}
      >
        <DragIndicatorIcon fontSize="small" sx={{ color: "text.secondary", flexShrink: 0 }} />
        {isConnecting ? (
          <CircularProgress size={16} color="success" sx={{ flexShrink: 0 }} />
        ) : (
          <PhoneInTalkIcon fontSize="small" color="success" sx={{ flexShrink: 0 }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {headline}
          </Typography>
          {!collapsed && subtitle ? (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <Tooltip title={collapsed ? "Expandir fila" : "Recolher"}>
          <IconButton
            size="small"
            aria-label={collapsed ? "Expandir fila" : "Recolher fila"}
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? <UnfoldMoreIcon fontSize="small" /> : <UnfoldLessIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {collapsed ? (
        <Box sx={{ px: 1.25, pb: 1.25, pt: 0.5 }}>
          {isConnecting ? (
            <Typography variant="caption" color="text.secondary">
              Conectando com o cliente…
            </Typography>
          ) : primaryClaim ? (
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="medium"
              disabled={Boolean(claimingId)}
              onClick={() => onClaim(primaryClaim)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Falar agora
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Aguardando — arraste pela barra acima
            </Typography>
          )}
        </Box>
      ) : null}

      <Collapse in={!collapsed} timeout={180}>
        <Stack spacing={1} sx={{ p: 1.25, maxHeight: 340, overflow: "auto" }}>
          {softphoneError ? (
            <Stack direction="row" alignItems="flex-start" spacing={0.5}>
              <Typography variant="caption" color="error" sx={{ flex: 1, minWidth: 0 }}>
                {softphoneError.message ||
                  `Softphone desconectado${
                    softphoneError.code ? ` (${softphoneError.code})` : ""
                  }. Recarregue a página ou use o celular cadastrado no perfil.`}
              </Typography>
              {typeof clearSoftphoneError === "function" ? (
                <Button
                  size="small"
                  color="error"
                  onClick={() => clearSoftphoneError()}
                  sx={{ minWidth: 0, px: 0.75, textTransform: "none", flexShrink: 0 }}
                >
                  Fechar
                </Button>
              ) : null}
            </Stack>
          ) : null}
          {info ? (
            <Typography variant="caption" color="success.main">
              {info}
            </Typography>
          ) : null}
          {error ? (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          ) : null}

          {isConnecting ? (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "success.light",
                borderRadius: 1.5,
                p: 1.5,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(46,125,50,0.12)"
                    : "rgba(46,125,50,0.06)",
                textAlign: "center",
              }}
            >
              <CircularProgress size={22} color="success" sx={{ mb: 1 }} />
              <Typography variant="body2" fontWeight={700}>
                Conectando com o cliente
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Já pedimos a ligação — em instantes você fala com ele
              </Typography>
            </Box>
          ) : null}

          {!isConnecting
            ? items.map((item) => {
                const waiting = waitLabel(item.enqueuedAt, nowMs);
                const isYours = item.queueScope === "user";
                const canClaim = Boolean(item.canClaim);
                const title = isYours
                  ? "Para você"
                  : canClaim
                    ? "Fila da equipe"
                    : "Fila da equipe";
                const help = canClaim
                  ? "Cliente na espera — um toque e você fala"
                  : isYours
                    ? "Chega no seu softphone quando você estiver livre"
                    : "Vai para o próximo da equipe que estiver livre";

                return (
                  <Box
                    key={item.id}
                    sx={{
                      border: "1px solid",
                      borderColor: canClaim ? "success.light" : "divider",
                      borderRadius: 1.5,
                      p: 1.25,
                      bgcolor: (theme) =>
                        canClaim
                          ? theme.palette.mode === "dark"
                            ? "rgba(46,125,50,0.12)"
                            : "rgba(46,125,50,0.06)"
                          : "transparent",
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {item.channelName ? `${item.channelName} · ` : ""}
                          Solicitação #{item.leadId}
                          {waiting ? ` · esperando ${waiting}` : ""}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.35 }}
                        >
                          {help}
                        </Typography>
                      </Box>
                      {item.leadId ? (
                        <Tooltip title="Abrir solicitação">
                          <IconButton
                            size="small"
                            aria-label="Abrir solicitação"
                            onClick={() => openLead(item.leadId)}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Stack>

                    {canClaim ? (
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        size="medium"
                        sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
                        disabled={Boolean(claimingId)}
                        onClick={() => onClaim(item)}
                      >
                        {claimingId === item.id ? "Conectando…" : "Falar agora"}
                      </Button>
                    ) : null}
                  </Box>
                );
              })
            : null}
        </Stack>
      </Collapse>
    </Paper>
  );
}
